//===========================================
//
// Cookie Management Utility
//
//===========================================

class CookieManager {
  constructor() {
    this.cookieName = 'cfsr_gate_submitted';
    this.cookieDuration = 365; // days
  }

  /**
   * Set a cookie with the specified name, value, and expiration
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Number of days until expiration
   */
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const sameSite = '; SameSite=Strict';

    document.cookie = `${name}=${value}; ${expires}; path=/${secure}${sameSite}`;
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }

    return null;
  }

  /**
   * Delete a cookie by name
   * @param {string} name - Cookie name
   */
  deleteCookie(name) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const sameSite = '; SameSite=Strict';
    document.cookie = `${name}=; Max-Age=-99999999; path=/${secure}${sameSite}`;
  }

  /**
   * Check if the gate has been submitted
   * @returns {boolean} True if gate was previously submitted
   */
  isGateSubmitted() {
    return this.getCookie(this.cookieName) !== null;
  }

  /**
   * Mark the gate as submitted
   * @param {object} submissionData - Optional data to store with the submission
   */
  markGateAsSubmitted(submissionData = {}) {
    const data = {
      timestamp: new Date().toISOString(),
      ...submissionData
    };

    this.setCookie(this.cookieName, JSON.stringify(data), this.cookieDuration);
  }

  /**
   * Get gate submission data
   * @returns {object|null} Submission data or null if not found
   */
  getGateSubmissionData() {
    const cookieValue = this.getCookie(this.cookieName);

    if (!cookieValue) {
      return null;
    }

    try {
      return JSON.parse(cookieValue);
    } catch (e) {
      // If the cookie value is not valid JSON, return it as-is
      return { value: cookieValue };
    }
  }

  /**
   * Clear the gate submission cookie
   */
  clearGateSubmission() {
    this.deleteCookie(this.cookieName);
  }

  /**
   * Check if the visitor should pass the gate without seeing it
   * @returns {boolean} True if the gate was submitted, or dismissed within 24 hours
   */
  isGateCleared() {
    return this.isGateSubmitted() || this.isSoftGateDismissed();
  }

  /**
   * Check if the soft gate has been dismissed
   * @returns {boolean} True if soft gate was dismissed within 24 hours
   */
  isSoftGateDismissed() {
    const cookieValue = this.getCookie('cfsr_soft_gate_dismissed');
    if (!cookieValue) {
      return false;
    }

    try {
      const data = JSON.parse(cookieValue);
      const dismissedAt = new Date(data.timestamp);
      const now = new Date();
      const hoursDiff = (now - dismissedAt) / (1000 * 60 * 60);

      // Return true if dismissed within 24 hours
      return hoursDiff < 24;
    } catch (e) {
      // If cookie is invalid, treat as not dismissed
      return false;
    }
  }

  /**
   * Mark the soft gate as dismissed
   */
  markSoftGateAsDismissed() {
    const data = {
      timestamp: new Date().toISOString()
    };

    // Set cookie for 24 hours
    this.setCookie('cfsr_soft_gate_dismissed', JSON.stringify(data), 1);
  }
}

// Export for use in other modules
export { CookieManager };

// Make available globally if needed
if (typeof window !== 'undefined') {
  window.CookieManager = CookieManager;
}
